// src/pages/admin/DesignForm.jsx
import { useState, useRef } from "react";
import { B, fonts, Input, Btn } from "../../components/UI";
import { addDesign, updateDesign } from "../../firebase/services";

export default function DesignForm({ initial, categories, onDone, showToast, onSavingChange }) {
  const blank = {
    price: "", colors: "", fabric: "",
    work: "", sizes: "S, M, L, XL, XXL", kurtilength: "", bottomLength: "",
    dupattaDetails: "", setType: "", setPieces: "", categoryId: "", categorySlug: "", badge: "",
    design_number: "",
    visibleOnWebsite: false,
  };

  const [form, setForm] = useState({ ...blank, ...(initial || {}) });
  const [saving, setSaving] = useState(false);

  // Existing image URLs (from Firestore) — shown as thumbnails
  const [existingImgs, setExistingImgs] = useState(
    initial?.images || (initial?.imageUrl ? [initial.imageUrl] : [])
  );

  // New File objects selected by user
  const [newFiles, setNewFiles] = useState([]);

  const fileInputRef = useRef();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCatChange = (id) => {
    const cat = categories.find(c => c.id === id);
    set("categoryId", id);
    set("categorySlug", cat?.slug || "");
  };

  // Called when user picks files — APPEND to existing selection
  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files);
    setNewFiles(prev => [...prev, ...picked]);
    // Reset input so same file can be re-selected if needed
    e.target.value = "";
  };

  const removeExisting = (idx) => {
    setExistingImgs(prev => prev.filter((_, i) => i !== idx));
  };

  const removeNew = (idx) => {
    setNewFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!form.price) return;
    setSaving(true);
    onSavingChange?.(true);
    try {
      if (initial) {
        // Pass existingImgs so updateDesign knows what to keep
        await updateDesign(initial.id, { ...form }, existingImgs, newFiles);
      } else {
        await addDesign(form, newFiles);
      }
      showToast(initial ? "Design updated ✓" : "Design added ✓");
      onDone();
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  const totalImages = existingImgs.length + newFiles.length;

  return (
    <div style={{ background: B.orangeFaint, border: `1.5px solid ${B.orangeMid}`, borderRadius: 14, padding: 24, marginBottom: 24 }}>
      <div style={{ fontWeight: 800, fontSize: 16, color: B.text, marginBottom: 20, fontFamily: fonts.adminDisplay }}>
        {initial ? "✏️ Edit Design" : "✨ Add New Design"}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
        <Input label="Price" value={form.price} onChange={e => set("price", e.target.value)} placeholder="e.g. ₹1,299" />
        <Input
          label="Design Number (optional)"
          value={form.design_number || ""}
          onChange={e => set("design_number", e.target.value)}
          placeholder="e.g. QTN-10045"
        />
        <Input label="Available Colors" value={form.colors} onChange={e => set("colors", e.target.value)} placeholder="e.g. Purple, Pink, Teal" />
        <Input label="Fabric" value={form.fabric} onChange={e => set("fabric", e.target.value)} placeholder="e.g. Pure Cotton" />
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 5, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>Set Type</label>
          <select value={form.setType || ""} onChange={e => set("setType", e.target.value)}
            style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "10px 13px", color: B.text, fontSize: 13, fontFamily: fonts.body, outline: "none" }}>
            <option value="">-- Select Set Type --</option>
            <option value="colors">Colors</option>
            <option value="sizes">Sizes</option>
          </select>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 5, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>Set Pieces</label>
          <select value={form.setPieces || ""} onChange={e => set("setPieces", e.target.value)}
            style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "10px 13px", color: B.text, fontSize: 13, fontFamily: fonts.body, outline: "none" }}>
            <option value="">-- Select Pieces --</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
          </select>
        </div>
        <Input label="Work / Embroidery" value={form.work} onChange={e => set("work", e.target.value)} placeholder="e.g. Mirror Work" />
        <Input label="Kurti Length" value={form.kurtilength} onChange={e => set("kurtilength", e.target.value)} placeholder='e.g. 44"' />
        <Input label="Bottom Length" value={form.bottomLength} onChange={e => set("bottomLength", e.target.value)} placeholder='e.g. 38" with pocket' />
        <div>
          <Input label="Dupatta Details" value={form.dupattaDetails} onChange={e => set("dupattaDetails", e.target.value)} placeholder='e.g. 2.25mt Mal Cotton — leave blank if not applicable' />
        </div>
        <Input label="Sizes" value={form.sizes} onChange={e => set("sizes", e.target.value)} />


        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              background: B.white,
              border: `1.5px solid ${B.border}`,
              borderRadius: 10,
              padding: "12px 14px",
              cursor: "pointer",
              fontFamily: fonts.body
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: B.textMid,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  marginBottom: 4
                }}
              >
                Frontend Visibility
              </div>
              <div style={{ fontSize: 12, color: B.text }}>
                {form.visibleOnWebsite ? "Visible on website" : "Hidden from website"}
              </div>
            </div>

            {/* SWITCH */}
            <div
              onClick={() => set("visibleOnWebsite", !form.visibleOnWebsite)}
              style={{
                width: 42,
                height: 22,
                borderRadius: 999,
                background: form.visibleOnWebsite ? B.orange : "#ccc",
                position: "relative",
                transition: "background 0.25s ease",
                cursor: "pointer",
                flexShrink: 0
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 2,
                  left: form.visibleOnWebsite ? 22 : 2,
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: "#fff",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  transition: "all 0.25s ease"
                }}
              />
            </div>
          </label>
        </div>

        {/* Category */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 5, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>Category</label>
          <select value={form.categoryId} onChange={e => handleCatChange(e.target.value)}
            style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "10px 13px", color: B.text, fontSize: 13, fontFamily: fonts.body, outline: "none" }}>
            <option value="">-- Select Category --</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* Badge */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 5, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>Badge</label>
          <select value={form.badge} onChange={e => set("badge", e.target.value)}
            style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "10px 13px", color: B.text, fontSize: 13, fontFamily: fonts.body, outline: "none" }}>
            <option value="">No Badge</option>
            <option value="new">🟢 New Arrival</option>
            <option value="bestseller">⭐ Bestseller</option>
            <option value="sale">🔴 Sale</option>
            <option value="featured">🟠 Featured</option>
          </select>
        </div>
      </div>

      {/* ── Image Section ── */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: B.textMid, marginBottom: 10, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: fonts.body }}>
          Photos ({totalImages} total) — first photo is the primary display image
        </div>

        {/* Thumbnails row */}
        {totalImages > 0 && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>

            {/* Existing URLs */}
            {existingImgs.map((src, i) => (
              <div key={`ex-${i}`} style={{ position: "relative", width: 72, height: 88 }}>
                <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8, border: `2px solid ${i === 0 && newFiles.length === 0 ? B.orange : B.borderLight}` }} />
                {i === 0 && newFiles.length === 0 && (
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: B.orange, color: "#fff", fontSize: 8, textAlign: "center", borderRadius: "0 0 6px 6px", padding: "1px 0", fontFamily: fonts.body }}>Primary</div>
                )}
                <button onClick={() => removeExisting(i)}
                  style={{ position: "absolute", top: -7, right: -7, width: 20, height: 20, borderRadius: "50%", background: B.red, color: "#fff", border: "2px solid #fff", cursor: "pointer", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fonts.body }}>✕</button>
              </div>
            ))}

            {/* New file previews */}
            {newFiles.map((file, i) => (
              <div key={`new-${i}`} style={{ position: "relative", width: 72, height: 88 }}>
                <img src={URL.createObjectURL(file)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8, border: `2px solid ${existingImgs.length === 0 && i === 0 ? B.orange : B.borderLight}` }} />
                {existingImgs.length === 0 && i === 0 && (
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: B.orange, color: "#fff", fontSize: 8, textAlign: "center", borderRadius: "0 0 6px 6px", padding: "1px 0", fontFamily: fonts.body }}>Primary</div>
                )}
                <div style={{ position: "absolute", top: -7, left: -7, background: "#25D366", color: "#fff", borderRadius: "50%", width: 16, height: 16, fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>✓</div>
                <button onClick={() => removeNew(i)}
                  style={{ position: "absolute", top: -7, right: -7, width: 20, height: 20, borderRadius: "50%", background: B.red, color: "#fff", border: "2px solid #fff", cursor: "pointer", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Upload button */}
        <div
          onClick={() => fileInputRef.current?.click()}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, background: B.white, border: `2px dashed ${B.orange}77`, borderRadius: 10, padding: "10px 20px", cursor: "pointer", color: B.orange, fontSize: 13, fontWeight: 600, fontFamily: fonts.body, transition: "all 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.background = B.orangeFaint}
          onMouseLeave={e => e.currentTarget.style.background = B.white}
        >
          <span style={{ fontSize: 18 }}>📷</span>
          {totalImages === 0 ? "Add Photos" : "Add More Photos"}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <p style={{ fontSize: 11, color: B.textLight, marginTop: 6, fontFamily: fonts.body }}>
          You can select multiple photos at once, or click again to add more.
        </p>
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn onClick={onDone} variant="ghost">Cancel</Btn>
        <Btn onClick={handleSave} disabled={!form.price || saving} variant="primary">
          {saving ? "Saving..." : initial ? "Save Changes" : "Add Design"}
        </Btn>
      </div>
    </div>
  );
}
